import React, { useState } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    Paper,
    Grid,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SearchIcon from "@mui/icons-material/Search";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import ApiService from "../../../services/categoryManagementApi";

const { reportsAndAnalyticsApi } = ApiService;

// ---- UTC helpers ----
function toUTCStart(date) {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
}
function toUTCEnd(date) {
    if (!date) return null;
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
}

const UserIssueReport = () => {
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [search, setSearch] = useState("");
    const [reportGenerated, setReportGenerated] = useState(false);
    const [expandedRows, setExpandedRows] = useState({});

    // Date range validation
    const isDateRangeValid =
        fromDate &&
        toDate &&
        !isNaN(new Date(fromDate)) &&
        !isNaN(new Date(toDate)) &&
        toDate >= fromDate;

    const fetchReport = async () => {
        if (!isDateRangeValid) {
            toast.error("Please select a valid date range.");
            return;
        }
        setLoading(true);
        try {
            const response = await reportsAndAnalyticsApi.getUserReport({
                type: "userIssues",
                startDate: toUTCStart(new Date(fromDate)).toISOString(),
                endDate: toUTCEnd(new Date(toDate)).toISOString(),
                search,
            });
            setReportData(response?.data?.data?.result || []);
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to fetch user issues report");
            console.error("Error fetching user issues report:", error);
        } finally {
            setReportGenerated(true);
            setLoading(false);
        }
    };

    const handleReset = () => {
        setFromDate(null);
        setToDate(null);
        setSearch("");
        setReportData([]);
        setReportGenerated(false);
        setExpandedRows({});
    };

    const exportToExcel = () => {
        const exportData = reportData.map((issue) => ({
            Name: issue.name || "N/A",
            Email: issue.email || "N/A",
            Subject: issue.subject || "N/A",
            Description: issue.description || "N/A",
            "Query Status": issue.queryStatus || "N/A",
            "Created At": issue.createdAt ? new Date(issue.createdAt).toLocaleString() : "N/A",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "User Issues Report");
        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(blob, `UserIssueReport_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    const toggleExpand = (index, field) => {
        setExpandedRows((prev) => ({
            ...prev,
            [`${index}-${field}`]: !prev[`${index}-${field}`],
        }));
    };

    const getTruncatedContent = (text, isExpanded, index, field) => {
        if (!text) return "N/A";
        const words = text.split(" ");
        const isLong = words.length > 40;
        if (!isLong) return text;
        return (
            <>
                {isExpanded ? words.join(" ") : words.slice(0, 40).join(" ") + "..."}{" "}
                <Button size="small" onClick={() => toggleExpand(index, field)}>
                    {isExpanded ? "Show Less" : "Show More"}
                </Button>
            </>
        );
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box p={3}>
                <Typography variant="h5" gutterBottom>User Issues Report</Typography>

                <Grid container spacing={2} alignItems="center" mb={2}>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="From Date"
                            value={fromDate}
                            onChange={val => {
                                setFromDate(val);
                                if (toDate && val && toDate < val) setToDate(null);
                            }}
                            maxDate={toDate || undefined}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <DatePicker
                            label="To Date"
                            value={toDate}
                            onChange={val => setToDate(val)}
                            minDate={fromDate || undefined}
                            maxDate={new Date()}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6} md={1.5}>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={fetchReport}
                            disabled={loading || !isDateRangeValid}
                            sx={{ height: 48 }}
                        >
                            {loading ? "Loading..." : "Generate Report"}
                        </Button>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="secondary"
                            onClick={handleReset}
                            sx={{ height: 48 }}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>

                {reportData.length > 0 ? (
                    <>
                        <Button
                            variant="contained"
                            onClick={exportToExcel}
                            sx={{ mb: 2 }}
                            disabled={reportData.length === 0}
                        >
                            Export Excel
                        </Button>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Subject</TableCell>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Query Status</TableCell>
                                        <TableCell>Created At</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {reportData.map((issue, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{issue.name || "N/A"}</TableCell>
                                            <TableCell>{issue.email || "N/A"}</TableCell>
                                            <TableCell>
                                                {getTruncatedContent(
                                                    issue.subject || "N/A",
                                                    expandedRows[`${idx}-subject`],
                                                    idx,
                                                    "subject"
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {getTruncatedContent(
                                                    issue.description || "N/A",
                                                    expandedRows[`${idx}-description`],
                                                    idx,
                                                    "description"
                                                )}
                                            </TableCell>
                                            <TableCell>{issue.queryStatus || "N/A"}</TableCell>
                                            <TableCell>
                                                {issue.createdAt
                                                    ? new Date(issue.createdAt).toLocaleString()
                                                    : "N/A"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                ) : reportGenerated ? (
                    <Typography sx={{ mt: 3 }} color="text.secondary" align="center">
                        No data found.
                    </Typography>
                ) : (
                    <Paper
                        elevation={2}
                        sx={{
                            mt: 4,
                            width: "100%",
                            minHeight: "35vh",            // Adjust for desired min height
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            bgcolor: "#fafbfc",
                            boxSizing: "border-box",
                            p: { xs: 2, sm: 4 },
                        }}
                    >
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Select a date range above and click <strong>Generate Report</strong> to view submitted user issues.
                        </Typography>

                    </Paper>

                )}
            </Box>
        </LocalizationProvider>
    );
};

export default UserIssueReport;
